import { AddonUsageType, SubscriptionModel } from '../model/SubscriptionModel';
import { PageBillingType } from './connected-page';

export type SubscriptionAccount = {
  cardType: string;
  editBillingLink: string;
  lastCardNumbers: string;
  month: number;
  year: number;
};

export type ExpiredSubscription = {
  id: number;
  expiresAt: string | null;
  canceledAt: string | null;
  createdAt: string;
  activatedAt: string | null;
};

export type SubscriptionPlan = {
  name: string;
  planCode: string;
  priceUsd: number;
};

export type ResponseInvoice = {
  invoiceNumber: number;
  invoiceLink: string;
  subscriptionIds: number[];
  totalCost: number;
  dueOn: string;
  createdAt: string;
  status: string;
  oneTimePayment: boolean;
  overageCharge: boolean;
};

export type FormattedInvoice = ResponseInvoice & {
  id: number;
  formattedTotalCost: number;
};

export type SubscriptionResponse = {
  subscriptions?: SubscriptionModel[];
  expiredSubscriptions?: ExpiredSubscription[];
  account: SubscriptionAccount;
  errorCode: any;
  errors: any[];
  invoices: ResponseInvoice[];
  success: boolean;
  coupon?: Coupon;
};

export type Discount = {
  type: string;
  value: number;
  paymentDate: string;
};

export type Coupon = {
  code: string;
  redeemable: boolean;
  discount: Discount;
};

export enum PlanTypeBE {
  pro = 'pro',
  team = 'team',
  teamSpecial = 'team_special',
  proSpecial = 'pro_special',
  teamCustom = 'team_custom',
}

export enum PlanBillingPeriodBE {
  month = 'month',
  year = 'year',
}

export type PlanFeatures = {
  emailWidget: boolean;
  contactsExport: boolean;
  unlimitedTriggers: boolean;
};

export const NoFeatures: PlanFeatures = {
  emailWidget: false,
  contactsExport: false,
  unlimitedTriggers: false,
};

export const FullFeatures: PlanFeatures = {
  emailWidget: true,
  contactsExport: true,
  unlimitedTriggers: true,
};

// TODO: this particular mapping should be removed in favor of BE returned plan features in each page (once implemented)
export const DefaultPlansFeatures: Partial<
  Record<keyof PlansMapping, PlanFeatures>
> = {
  wp: {
    emailWidget: true,
    contactsExport: true,
    unlimitedTriggers: true,
  },
};

export enum PlanSubtypes {
  team2020 = 'team2020',
  team2019 = 'team2019',
  teamCustom = 'team_custom',
  unicorn = 'unicorn',
  instachamp = 'instachamp',
  pro = 'pro',
  proLegacy = 'pro_legacy',
  billing2023 = 'billing2023',
  billing2023v2 = 'billing2023_v2',
  free2023 = 'free2023',
  starter2023 = 'starter2023',
  pro2023 = 'pro2023',
  introductory2023 = 'introductory2023',
  billing2025 = 'billing2025',
  billing2025v2 = 'billing2025_v2', // ESP billing
  level1 = 'level1',
  level2 = 'level2',
  level3 = 'level3',
}

export const planSubtypesByType = {
  team: [PlanSubtypes.team2019, PlanSubtypes.team2020],
  unicorn: [PlanSubtypes.unicorn],
  pro: [PlanSubtypes.pro],
  starterV2: [PlanSubtypes.starter2023, PlanSubtypes.billing2023v2],
  proV2: [PlanSubtypes.pro2023, PlanSubtypes.billing2023v2],
  nonSQLIntroductory: [
    PlanSubtypes.billing2023,
    PlanSubtypes.teamCustom,
    PlanSubtypes.introductory2023,
  ],
  restore: [
    PlanSubtypes.billing2023,
    PlanSubtypes.billing2023v2,
    PlanSubtypes.level1,
  ],
  agencyLevel1: [
    PlanSubtypes.teamCustom,
    PlanSubtypes.billing2023,
    PlanSubtypes.billing2023v2,
    PlanSubtypes.level1,
    PlanSubtypes.billing2025,
  ],
  agencyLevel2: [
    PlanSubtypes.teamCustom,
    PlanSubtypes.billing2023,
    PlanSubtypes.billing2023v2,
    PlanSubtypes.level2,
    PlanSubtypes.billing2025,
  ],
  agencyLevel3: [
    PlanSubtypes.teamCustom,
    PlanSubtypes.billing2023,
    PlanSubtypes.billing2023v2,
    PlanSubtypes.level3,
    PlanSubtypes.billing2025,
  ],
};

export const ADDON_SETTINGS_KEYS = {
  emailSends: 'emailSends' as AddonSettingsKeyType,
  mercuryLeads: 'mercuryLeads' as AddonSettingsKeyType,
  smsSends: 'smsSends' as AddonSettingsKeyType,
  socialSends: 'socialSends' as AddonSettingsKeyType,
  xrayLeads: 'xrayLeads' as AddonSettingsKeyType,
  contactsManagement: 'contactsManagement' as AddonSettingsKeyType,
  resignalLeads: 'resignalLeads' as AddonSettingsKeyType,
};

export type AddonSettingsKeyType =
  | 'emailSends'
  | 'mercuryLeads'
  | 'smsSends'
  | 'socialSends'
  | 'xrayLeads'
  | 'contactsManagement'
  | 'resignalLeads';

export type AddonSettingsValueType = {
  ending_quantity?: number;
  package_value?: string; // FE only field
  unit_amount_in_cents: {
    USD: number;
  };
};
export type AddonSettingsType = {
  [key in keyof AddonSettingsKeyType]: string;
};

export type Plan = {
  id: number;
  canUpgradeFromFree: boolean;
  cancellableInUi: boolean;
  downgradePlansIds: number[];
  name: string;
  planCode: string;
  legacy: boolean;
  outgoingMessagesLimit: number;
  team: boolean;
  upgradePlansIds: number[];
  unitAmountInCents: number;
  annualUnitAmountInCents: number;
  oldUnitAmountInCents: number;
  shortName: string;
  planType: PlanTypeBE;
  pagesLimit?: number | null;
  billingPeriod: PlanBillingPeriodBE;
  extraPageAddOnPrice?: number;
  userSeatsAddOnPrice: number;
  extraUserSeats: number;
  userSeatAddOnPrice: number;
  maxDisplayingSendAllotmentLevel: number;
  userSeats: number;
  sendAllotmentAddOnPrice: number;
  smsNumberAddOnPrice: number;
  tier: string;
  planSubtypes: PlanSubtypes[];
  addonSettings: AddonSettingsType;
  addonUsage: AddonUsageType;
};

export type EnhancedPlan = Plan & {
  pageBillingType: PageBillingType;
};

export type PlansMapping = {
  [key: string]: string | string[];
  pro: string | string[];
  unicorn: string | string[];
  team: string | string[];
  wp: string | string[];
  instachamp: string | string[];
  actualTeam: string;
};

export type UserSeatsMapping = {
  [key: string]: number;
  pro: number;
  unicorn: number;
  team: number;
};

export type PlansAllotments = {
  [key: string]: AllotmentDefinition;
};

export type AllotmentDefinition = {
  levels: number;
  start: number;
  msgPerLevel: number;
};

export type PlansPrices = Partial<{ [key in keyof PlansMapping]: number[] }>;

export type Subscription = {
  plans: Plan[];
  planInfo?: Plan;
  plansMapping?: PlansMapping;
  plansPrices?: PlansPrices;
  specialDeal?: boolean;
  offerTeam?: boolean;
  busy: boolean;
  token?: string | null;
  coupon?: Coupon | null;
  accountDetails?: SubscriptionResponse;
  planAllotments: PlansAllotments;
  annualPlanAllotments: PlansAllotments;
  plansUserSeats: UserSeatsMapping;
  priceForNextLevel: {
    price: number;
  };
  currentSubscription?: { [key: string]: SubscriptionModel };
  futurePaymentCredits: number;
};
